import { claimSubmitterName } from "../constants/claimConstants";
import { monthKeyOf } from "./claimRules";

/**
 * The filtering every conveyance claim list does — ESS, the manager queue and
 * the finance register alike.
 *
 * It happens in the browser, over the rows the screen already holds, because
 * the search endpoint takes no filter arguments: its three queries accept a
 * tenant, an employee and a page, and nothing else. Sending status or month to
 * it changes nothing, so the screens stopped pretending otherwise.
 *
 * The options offered are likewise derived from the rows, not declared: a
 * month with no claim in it is not worth a line in the dropdown, and a
 * colleague with no claim is not worth one either.
 *
 * ALL is the "not filtering" sentinel the screens already use.
 */

const ALL = "all";

/**
 * How a claim identifies its submitter for filtering.
 *
 * The employee id where the row carries one; otherwise the name, so a response
 * shape without ids still groups rows by person rather than collapsing them.
 * Option building and matching both go through this, so they cannot disagree.
 */
export function employeeKeyOf(claim) {
  const empId = claim?.empId ?? claim?.employeeId;
  if (empId != null && String(empId) !== "") return String(empId);
  return claimSubmitterName(claim, "");
}

/** The claim months present in these rows, newest first. */
export function monthKeysOf(claims) {
  const keys = new Set();
  (claims || []).forEach((claim) => {
    const key = monthKeyOf(claim?.claimMonth || claim?.claimDate);
    if (key) keys.add(key);
  });
  return [...keys].sort().reverse();
}

/** The people who raised these claims, by name. */
export function employeeOptionsOf(claims) {
  const byKey = new Map();
  (claims || []).forEach((claim) => {
    const value = employeeKeyOf(claim);
    if (!value) return;
    if (!byKey.has(value)) {
      byKey.set(value, { value, label: claimSubmitterName(claim, value) });
    }
  });
  return [...byKey.values()].sort((a, b) => String(a.label).localeCompare(String(b.label)));
}

/**
 * The rows a list should show. Every filter left at ALL is simply not applied,
 * and they combine: what survives satisfies all of them.
 *
 * `search` matches the claim number or the submitter's name — the two things
 * the box says it searches.
 */
export function filterClaims(claims, { status, month, category, employee, search } = {}) {
  const term = String(search ?? "").trim().toLowerCase();

  return (claims || []).filter((claim) => {
    if (status && status !== ALL && claim?.claimStatus !== status) return false;
    if (category && category !== ALL && claim?.category !== category) return false;

    if (month && month !== ALL) {
      if (monthKeyOf(claim?.claimMonth || claim?.claimDate) !== month) return false;
    }

    if (employee && employee !== ALL && employeeKeyOf(claim) !== employee) return false;

    if (term) {
      const haystack = [claim?.claimNo, `#${claim?.claimId ?? ""}`, claimSubmitterName(claim, "")]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      if (!haystack.includes(term)) return false;
    }

    return true;
  });
}
